import type { CSSProperties, MouseEventHandler, ReactNode } from 'react';

import { useGaChartPreviewMeta } from '../../../global-ai-assistant/ga-chart-preview-meta-context';
import { GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT } from '../../constants/panelWidths';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import styles from './ChartCardShell.module.css';
import { useSafeChart } from './ChartProvider';
import { ChartCard } from './ChartRoot/Chart';
import { ExpandModalFallback } from './chart-menu/ExpandModal';

export const ChartCardShell = ({
  zIndex = 0,
  onClick,
  item: itemProp,
  children,
}: {
  zIndex?: number;
  onClick?: MouseEventHandler<HTMLDivElement>;
  /**
   * Passed explicitly where the caller has it. `useSafeChart` is only a
   * fallback: this shell renders outside `ChartProvider` on the main chart
   * path, so relying on context alone left every card without the attributes
   * and made the Data Inspector highlight a no-op.
   */
  item?: PositionedItem;
  children: ReactNode;
}) => {
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;
  // Lets the Data Inspector highlight the card an operation feeds, and the
  // cards plotting a given breakdown when its category is hovered.
  const { item: itemFromContext } = useSafeChart();
  const item = itemProp ?? itemFromContext;

  return (
    <>
      <ChartCard onClick={onClick}>
        <div
          data-testid="card-body"
          data-chart-item-id={item?.id}
          data-chart-dimension={item?.plot?.secondaryDimension}
          data-ga-preview={isGlobalAiChartPreview || undefined}
          className={styles.body}
          style={
            {
              ...(zIndex && { '--card-body-z-index': zIndex }),
              ...(isGlobalAiChartPreview && {
                '--card-body-min-height': GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT,
              }),
            } as CSSProperties
          }
        >
          {children}
        </div>
      </ChartCard>
      {/* The chart's loading, error or no-data state stands in for it in the
          open Expand Plot modal too. */}
      <ExpandModalFallback>{children}</ExpandModalFallback>
    </>
  );
};
